import React, { useEffect, useMemo } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../../../../hooks/hooks';
import { Day, IdNameModel } from '../../../../../../../features/settings/workcalendar/types';
import { updateDay, getWorkCalendar } from '../../../../../../../features/settings/workcalendar';
import BaseForm from '../../../../../../../components/shared/form-controls/base-form';
import { DropdownField } from '../../../../../../../components/shared/nextgen/dropdown-field';
import { EditForm, DayType } from './types';
import { workHoursStringArray } from '../../../types';
import { NAVIGATION_ROUTES } from '../../../../../../../routes/NavigationRoute';
import { Lx } from '../../../../../../../i18n/consts';
import { canManageWorkCalendars } from '../../../../../../../features/profile/entityPermissions';

const validationScheme: Yup.Schema<EditForm> = Yup.object({
    dayType: Yup.object<IdNameModel>().shape({
        id: Yup.number().required(),
        name: Yup.string().required(),
    }).required(),
    customHours: Yup.object().shape({
        id: Yup.number().required(),
        name: Yup.string().optional(),
    }).optional(),
});

interface LocationState {
    workCalendarId: number;
    year: number;
    month: number;
    day: Day;
}

const EditWorkCalendarDayForm = (): JSX.Element | null => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const profile = useAppSelector(store => store.profile);
    const state = location.state as LocationState | undefined;
    const canEditWorkCalendars = canManageWorkCalendars(profile);

    const dayTypes: IdNameModel[] = [
        { id: DayType.IsWorkingDay, name: intl.formatMessage({ id: Lx.Calendar.WORKING_DAY }) },
        { id: DayType.IsShortDay, name: intl.formatMessage({ id: Lx.Calendar.SHORT_DAY })  },
        { id: DayType.IsDayOff, name: intl.formatMessage({ id: Lx.Calendar.DAY_OFF })  }
    ];


    useEffect(() => {
        if (!canEditWorkCalendars || !state) {
            navigate(NAVIGATION_ROUTES.SETTINGS_CALENDAR, { replace: true });
        }
    }, [canEditWorkCalendars, navigate, state]);

    const { workCalendarId, year, month, day } = state ?? {
        workCalendarId: 0,
        year: 0,
        month: 0,
        day: { day: 0, customHours: 0, isHoliday: false, isShort: false } as Day,
    };

    const getDayType = (targetDay: Day): IdNameModel => {
        if (targetDay.isShort) {
            return dayTypes.find(x => x.id === DayType.IsShortDay)!;
        } else if (targetDay.isHoliday) {
            return dayTypes.find(x => x.id === DayType.IsDayOff)!;
        }

        return dayTypes.find(x => x.id === DayType.IsWorkingDay)!;
    };

    const getHours = (hour: number): IdNameModel => {
        return workHoursStringArray.find(x => x.id === hour) ?? { id: 0, name: '' };
    };

    const handleClose = () => {
        navigate(NAVIGATION_ROUTES.SETTINGS_CALENDAR, {
            state: {
                selectedCalendarId: workCalendarId,
            },
        });
    };

    const dateLabel = useMemo(() => {
        if (!state) {
            return '';
        }
        return moment({ year, month: month - 1, day: day.day }).format('MMMM Do, YYYY');
    }, [state, day.day, month, year]);

    const initialValues: EditForm = {
        dayType: getDayType(day),
        customHours: getHours(day.customHours),
    };

    const save = async (values: EditForm) => {
        const changedDay: Day = {
            day: day.day,
            customHours: values.dayType.id === DayType.IsDayOff ? 0 : values.customHours?.id ?? 0,
            isShort: values.dayType.id === DayType.IsShortDay,
            isHoliday: values.dayType.id === DayType.IsDayOff,
        };

        await dispatch(updateDay({ workCalendarId, year, month, day: changedDay })).unwrap();
        await dispatch(getWorkCalendar({ workCalendarId, year })).unwrap();
    };

    if (!canEditWorkCalendars || !state) {
        return null;
    }

    return (
        <Formik initialValues={initialValues} validationSchema={validationScheme} onSubmit={save} enableReinitialize>
            <Form>
                <BaseForm
                    contentGap={24}
                    formType='edit'
                    formHeader='edit_date_settings'
                    elementName={dateLabel}
                    savingTitle='Day'
                    onClose={handleClose}
                    onAfterSave={handleClose}>
                    <div style={{ width: '100%', maxWidth: '500px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        <DropdownField required label={intl.formatMessage({ id: Lx.Calendar.DAY_TYPE })} name='dayType' items={dayTypes} />
                        <DropdownField
                            label={intl.formatMessage({ id: Lx.Calendar.WORK_HOURS })}
                            name='customHours'
                            items={workHoursStringArray}
                            description={intl.formatMessage({ id: Lx.Form.EMPTY_FIELD_ABILITY })}
                        />
                    </div>
                </BaseForm>
            </Form>
        </Formik>
    );
};

export default React.memo(EditWorkCalendarDayForm);
